import type { Frame, LayoutMode } from '~/types/pipeline';

/**
 * Apply a LayoutMode to a set of frames, returning new Frame objects with
 * spriteSourceSize.{x,y} set per the mode. Does not mutate input frames.
 *
 * - sequence: identity — return frames as-is (copied).
 * - grid:     arrange by index; cell size = max(trimRect.w) × max(trimRect.h).
 * - free:     user-specified row/col per frame.index; missing entries fall back to row-major order.
 */
export function applyLayout(frames: readonly Frame[], mode: LayoutMode): Frame[] {
  if (mode.kind === 'sequence') {
    return frames.map((f) => ({ ...f }));
  }

  const cellW = Math.max(...frames.map((f) => f.trimRect.w));
  const cellH = Math.max(...frames.map((f) => f.trimRect.h));

  if (mode.kind === 'grid') {
    const { cols, fill } = mode;
    return frames.map((frame, idx) => {
      const row = fill === 'row-major' ? Math.floor(idx / cols) : idx % mode.rows;
      const col = fill === 'row-major' ? idx % cols : Math.floor(idx / mode.rows);
      return place(frame, row, col, cellW, cellH);
    });
  }

  // free
  return frames.map((frame, index) => {
    const pos = mode.positions[frame.index];
    if (!pos) return place(frame, 0, index, cellW, cellH);
    return place(frame, pos.row, pos.col, cellW, cellH);
  });
}

function place(frame: Frame, row: number, col: number, cellW: number, cellH: number): Frame {
  return {
    ...frame,
    spriteSourceSize: {
      x: Math.round(col * cellW + (cellW - frame.trimRect.w) / 2),
      y: Math.round(row * cellH + (cellH - frame.trimRect.h) / 2),
      w: frame.trimRect.w,
      h: frame.trimRect.h,
    },
  };
}
